import { Link } from "react-router-dom";
import logo from "@/assets/logo.webp";
import { EqualHousingLogo, IDXDisclaimer } from "@/components/compliance";

const Footer = () => {
  const footerLinks = {
    company: [
      { name: "About Us", path: "/about" },
      { name: "Our Agents", path: "/agents" },
      { name: "Careers", path: "/careers" },
      { name: "Contact", path: "/contact" },
      { name: "Sold Properties", path: "/sold" },
    ],
    resources: [
      { name: "Buy a Home", path: "https://homes.prpsj.com/idx/results/listings?idxID=b004&a_propStatus%5B%5D=ACTIVE&a_propStatus%5B%5D=COMING%2BSOON", external: true },
      { name: "Sell Your Home", path: "/sell" },
      { name: "Communities", path: "/communities" },
      { name: "First Time Buyers", path: "/first-time-home-buyer-nj" },
      { name: "Closing Costs NJ", path: "/closing-costs-nj" },
      { name: "Home Value", path: "/home-value" },
      { name: "How to Buy", path: "/how-to-buy-a-house-nj" },
      { name: "Blog", path: "/blog" },
    ],
    legal: [
      { name: "Privacy Policy", path: "/privacy" },
      { name: "Terms of Service", path: "/terms" },
      { name: "Fair Housing", path: "/fair-housing" },
      { name: "Accessibility", path: "/accessibility" },
    ],
  };

  return (
    <footer className="border-t border-border bg-secondary/30">
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img
                src={logo}
                alt="Prime Realty Partners logo"
                width={160}
                height={64}
                className="h-16 w-auto object-contain"
              />
            </Link>
            <p className="text-sm text-muted-foreground mb-4">
              Experience You Can Trust. Results you Deserve.
            </p>
            {/* Brokerage Info */}
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium">Prime Realty Partners</p>
              <p>31 Walnut Ave, Bellmawr, NJ 08031</p>
              <p>(856) 210-3000</p>
              <p>Licensed Real Estate Broker</p>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Communities Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Communities</h4>
            <ul className="space-y-2">
              {[
                { name: "Cherry Hill", path: "/cherry-hill-homes-for-sale" },
                { name: "Moorestown", path: "/moorestown-homes-for-sale" },
                { name: "Mount Laurel", path: "/mount-laurel-homes-for-sale" },
                { name: "Marlton", path: "/marlton-homes-for-sale" },
                { name: "Voorhees", path: "/voorhees-homes-for-sale" },
                { name: "Haddonfield", path: "/haddonfield-homes-for-sale" },
                { name: "Washington Twp", path: "/washington-township-homes-for-sale" },
                { name: "Gloucester Twp", path: "/gloucester-township-homes-for-sale" },
                { name: "Deptford", path: "/deptford-homes-for-sale" },
                { name: "Collingswood", path: "/collingswood-homes-for-sale" },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Resources</h4>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  {'external' in link && link.external ? (
                    <a
                      href={link.path}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* MLS Membership */}
        <div className="mt-8 pt-8 border-t border-border">
          <p className="text-sm text-muted-foreground mb-4">
            Prime Realty Partners is a member of Bright MLS.
          </p>
          <div className="text-xs text-muted-foreground space-y-1 mb-4">
            <p>Data updated frequently from IDX Broker.</p>
          </div>
        </div>

        {/* IDX Disclaimers */}
        <div className="mt-6 pt-6 border-t border-border">
          <IDXDisclaimer variant="full" />
        </div>

        {/* Bottom Section */}
        <div className="mt-8 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <EqualHousingLogo size="md" className="text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                Equal Housing Opportunity
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/auth"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Agent Login
              </Link>
              <p className="text-xs text-muted-foreground text-center md:text-right">
                © 2026 Prime Realty Partners. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
